/**
 * Workflow List Page
 *
 * Displays a list of workflows with search, filtering, and actions.
 * Owner-only access.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import TopBar from '../../components/TopBar';
import { useTenant } from '../../contexts/TenantContext';
import { WorkflowUploadSidebar } from './WorkflowUploadSidebar';
import { WorkflowUpdateSidebar } from './WorkflowUpdateSidebar';
import { WorkflowDetailsSidebar } from './WorkflowDetailsSidebar';
import { WorkflowPresentationEditor } from './WorkflowPresentationEditor';


interface WorkflowMetadata {
  partitionKey: string;
  rowKey: string;
  displayName: string;
  key: string;
  status: 'active' | 'archived';
  tags: string;
  category?: string;
  description?: string;
  author?: string;
  version?: string;
  displayOrder?: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

interface WorkflowListProps {
  tenantId: string;
  apiBaseUrl: string;
  authToken: string;
}

export const WorkflowList: React.FC<WorkflowListProps> = ({ tenantId, apiBaseUrl, authToken }) => {
  const navigate = useNavigate();
  const { currentTenant, currentUser } = useTenant();
  const [workflows, setWorkflows] = useState<WorkflowMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [tagFilter, setTagFilter] = useState<string>('');

  // Available tags (extracted from workflows)
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  // Sidebar state
  const [showUploadSidebar, setShowUploadSidebar] = useState(false);
  const [showUpdateSidebar, setShowUpdateSidebar] = useState(false);
  const [showDetailsSidebar, setShowDetailsSidebar] = useState(false);
  const [showPresentationEditor, setShowPresentationEditor] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [selectedWorkflowName, setSelectedWorkflowName] = useState<string>('');

  // Fetch workflows
  useEffect(() => {
    fetchWorkflows();
  }, [statusFilter, tagFilter, searchQuery]);

  const fetchWorkflows = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('status', statusFilter);
      if (tagFilter) params.append('tags', tagFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await fetch(
        `${apiBaseUrl}/api/tenants/${tenantId}/workflows?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch workflows: ${response.statusText}`);
      }

      const data = await response.json();
      setWorkflows(data.workflows || []);

      // Extract unique tags
      const tags = new Set<string>();
      data.workflows.forEach((w: WorkflowMetadata) => {
        try {
          const workflowTags = JSON.parse(w.tags || '[]');
          workflowTags.forEach((tag: string) => tags.add(tag));
        } catch (e) {
          // Ignore parse errors
        }
      });
      setAvailableTags(Array.from(tags).sort());

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDetails = (workflowId: string) => {
    setSelectedWorkflowId(workflowId);
    setShowDetailsSidebar(true);
  };

  const handleDetailsClose = () => {
    setShowDetailsSidebar(false);
    setSelectedWorkflowId(null);
  };

  const handleDetailsUpdate = () => {
    fetchWorkflows();
  };

  const handleEdit = (workflowId: string) => {
    setSelectedWorkflowId(workflowId);
    setShowPresentationEditor(true);
  };

  const handlePresentationEditorClose = () => {
    setShowPresentationEditor(false);
    setSelectedWorkflowId(null);
  };

  const handlePresentationEditorUpdate = () => {
    fetchWorkflows();
  };

  const handleUpdate = (workflowId: string, displayName: string) => {
    setSelectedWorkflowId(workflowId);
    setSelectedWorkflowName(displayName);
    setShowUpdateSidebar(true);
  };

  const handleUpdateSidebarClose = () => {
    setShowUpdateSidebar(false);
    setSelectedWorkflowId(null);
    setSelectedWorkflowName('');
  };

  const handleUpdateSuccess = () => {
    setShowUpdateSidebar(false);
    setSelectedWorkflowId(null);
    setSelectedWorkflowName('');
    fetchWorkflows();
  };

  const handleDuplicate = async (workflowId: string, displayName: string) => {
    if (!window.confirm(`Duplicate workflow "${displayName}"?`)) return;

    try {
      const response = await fetch(
        `${apiBaseUrl}/api/workflows/${workflowId}/duplicate?tenantId=${encodeURIComponent(tenantId)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            displayName: `${displayName} (Copy)`
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to duplicate workflow: ${response.statusText}`);
      }

      alert('Workflow duplicated successfully!');
      fetchWorkflows();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleDelete = async (workflowId: string, displayName: string) => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete workflow "${displayName}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(
        `${apiBaseUrl}/api/workflows/${workflowId}?tenantId=${encodeURIComponent(tenantId)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to delete workflow: ${response.statusText}`);
      }

      alert('Workflow deleted successfully!');
      fetchWorkflows();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleUpload = () => {
    setShowUploadSidebar(true);
  };

  const handleUploadSuccess = () => {
    setShowUploadSidebar(false);
    fetchWorkflows();
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    // Reorder workflows array
    const reorderedWorkflows = Array.from(workflows);
    const [movedWorkflow] = reorderedWorkflows.splice(sourceIndex, 1);
    reorderedWorkflows.splice(destinationIndex, 0, movedWorkflow);

    // Update local state immediately for smooth UX
    setWorkflows(reorderedWorkflows);

    // Update displayOrder for all workflows
    try {
      const updates = reorderedWorkflows.map((workflow, index) => ({
        workflowId: workflow.rowKey,
        displayOrder: index
      }));

      const response = await fetch(`${apiBaseUrl}/api/tenants/${tenantId}/workflows/reorder`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ workflows: updates })
      });

      if (!response.ok) {
        throw new Error('Failed to update workflow order');
      }
    } catch (err: any) {
      alert(`Error updating order: ${err.message}`);
      // Revert to original order on error
      fetchWorkflows();
    }
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleDateString();
  };

  const parseTags = (tagsJson: string): string[] => {
    try {
      return JSON.parse(tagsJson || '[]');
    } catch (e) {
      return [];
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Top Bar */}
      <TopBar
        tenantName={currentTenant?.name || 'Workspace'}
        userEmail={currentUser?.email || ''}
      />

      {/* Main Content */}
      <div className="px-8 py-6 max-w-screen-xl mx-auto w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">Workflow Manager</h1>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => navigate('/assets')}
              className="px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-md text-label font-medium hover:bg-gray-50 hover:border-gray-400 transition-colors duration-fast shadow-xs">
              Asset Library
            </button>
            <button type="button" onClick={() => navigate('/products')}
              className="px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-md text-label font-medium hover:bg-gray-50 hover:border-gray-400 transition-colors duration-fast shadow-xs">
              Product Library
            </button>
            <button type="button" onClick={handleUpload}
              className="px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-label font-semibold transition-colors duration-fast shadow-sm">
              Upload Workflow
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <input type="text" placeholder="Search workflows…" value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast placeholder:text-gray-400" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}
            aria-label="Filter by status"
            className="px-4 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast select-input">
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="all">All</option>
          </select>
          <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}
            aria-label="Filter by tag"
            className="px-4 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast select-input">
            <option value="">All Tags</option>
            {availableTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
          </select>
        </div>

        {loading && <div className="py-10 text-center text-body text-gray-400">Loading workflows…</div>}
        {error   && <div className="py-10 text-center text-body text-error">Error: {error}</div>}

        {!loading && !error && workflows.length === 0 && (
          <div className="py-16 text-center">
            <p className="text-body text-gray-500 mb-5">No workflows found.</p>
            <button type="button" onClick={handleUpload}
              className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-label font-semibold transition-colors duration-fast">
              Upload Your First Workflow
            </button>
          </div>
        )}

        {!loading && !error && workflows.length > 0 && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <table className="w-full bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-10 px-3 py-3" aria-label="Drag to reorder"></th>
                  <th className="px-4 py-3 text-left text-meta font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-meta font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-meta font-semibold text-gray-500 uppercase tracking-wider">Tags</th>
                  <th className="px-4 py-3 text-left text-meta font-semibold text-gray-500 uppercase tracking-wider">Updated</th>
                  <th className="px-4 py-3 text-left text-meta font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <Droppable droppableId="workflows">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {workflows.map((workflow, index) => (
                      <Draggable key={workflow.rowKey} draggableId={workflow.rowKey} index={index}>
                        {(provided, snapshot) => (
                          <tr ref={provided.innerRef} {...provided.draggableProps}
                            className={`border-b border-gray-100 transition-colors duration-fast ${snapshot.isDragging ? 'bg-blue-50 shadow-md opacity-95' : 'hover:bg-gray-50'}`}>
                            <td {...provided.dragHandleProps}
                              className="px-3 py-4 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing text-center select-none">
                              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                <circle cx="4" cy="4" r="1.5" /><circle cx="4" cy="8" r="1.5" /><circle cx="4" cy="12" r="1.5" />
                                <circle cx="12" cy="4" r="1.5" /><circle cx="12" cy="8" r="1.5" /><circle cx="12" cy="12" r="1.5" />
                              </svg>
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-body font-medium text-gray-900">{workflow.displayName}</div>
                              {workflow.description && <div className="text-label text-gray-500 mt-0.5">{workflow.description}</div>}
                            </td>
                            <td className="px-4 py-4">
                              <span className={`inline-block px-3 py-1 rounded-full text-meta font-semibold capitalize ${
                                workflow.status === 'active'
                                  ? 'bg-success-light text-success-dark'
                                  : 'bg-gray-100 text-gray-500'
                              }`}>{workflow.status}</span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex flex-wrap gap-1.5">
                                {parseTags(workflow.tags).map(tag => (
                                  <span key={tag} className="px-2.5 py-0.5 bg-blue-50 text-blue-700 rounded-full text-meta font-medium">{tag}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-4 text-label text-gray-500 whitespace-nowrap">{formatDate(workflow.updatedAt)}</td>
                            <td className="px-4 py-4">
                              <div className="flex gap-2 flex-wrap">
                                {[
                                  { label: 'Details',   cls: 'hover:bg-gray-100',                                          fn: () => handleDetails(workflow.rowKey) },
                                  { label: 'Edit',      cls: 'hover:bg-gray-100',                                          fn: () => handleEdit(workflow.rowKey) },
                                  { label: 'Update',    cls: 'bg-info-light text-info border-info/30 hover:bg-blue-100',   fn: () => handleUpdate(workflow.rowKey, workflow.displayName) },
                                  { label: 'Duplicate', cls: 'hover:bg-gray-100',                                          fn: () => handleDuplicate(workflow.rowKey, workflow.displayName) },
                                  { label: 'Delete',    cls: 'text-error border-error/30 hover:bg-error-light',            fn: () => handleDelete(workflow.rowKey, workflow.displayName) },
                                ].map(({ label, cls, fn }) => (
                                  <button key={label} type="button" onClick={fn}
                                    className={`px-2.5 py-1 border border-gray-200 rounded-md text-meta font-medium text-gray-600 bg-gray-50 transition-colors duration-fast ${cls}`}>
                                    {label}
                                  </button>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </table>
          </DragDropContext>
        )}
      </div>

      {/* Upload Sidebar */}
      {showUploadSidebar && (
        <WorkflowUploadSidebar
          tenantId={tenantId}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          onClose={() => setShowUploadSidebar(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* Update Sidebar (Smart Update) */}
      {showUpdateSidebar && selectedWorkflowId && (
        <WorkflowUpdateSidebar
          workflowId={selectedWorkflowId}
          workflowName={selectedWorkflowName}
          tenantId={tenantId}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          onClose={handleUpdateSidebarClose}
          onSuccess={handleUpdateSuccess}
        />
      )}

      {/* Details Sidebar */}
      {showDetailsSidebar && selectedWorkflowId && (
        <WorkflowDetailsSidebar
          workflowId={selectedWorkflowId}
          tenantId={tenantId}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          onClose={handleDetailsClose}
          onUpdate={handleDetailsUpdate}
        />
      )}

      {/* Presentation Editor Sidebar */}
      {showPresentationEditor && selectedWorkflowId && (
        <WorkflowPresentationEditor
          workflowId={selectedWorkflowId}
          tenantId={tenantId}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          onClose={handlePresentationEditorClose}
          onUpdate={handlePresentationEditorUpdate}
        />
      )}
    </div>
  );
};

